import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
} from '@mui/material';
import { Add, Edit, Delete, SlideshowOutlined } from '@mui/icons-material';
import { useGetSlideTemplatesQuery } from '@/state/queries/slide-templates/use-get-slide-templates';
import { useCreateSlideTemplateMutation } from '@/state/mutations/slide-templates/use-create-slide-template';
import { useUpdateSlideTemplateMutation } from '@/state/mutations/slide-templates/use-update-slide-template';
import { useDeleteSlideTemplateMutation } from '@/state/mutations/slide-templates/use-delete-slide-template';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import SlideTemplateFormModal from './slide-template-form.modal';
import type { SlideTemplateDto } from '@/state/queries/slide-templates/use-get-slide-templates';
import type { CreateSlideTemplateDto } from '@/state/mutations/slide-templates/use-create-slide-template';

export default function SlideTemplatesPage() {
  const { data: templates, isLoading, error } = useGetSlideTemplatesQuery();
  const createMutation = useCreateSlideTemplateMutation();
  const updateMutation = useUpdateSlideTemplateMutation();
  const deleteMutation = useDeleteSlideTemplateMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SlideTemplateDto | null>(null);

  const handleOpenCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (t: SlideTemplateDto) => {
    setEditing(t);
    setModalOpen(true);
  };

  const handleSave = async (data: CreateSlideTemplateDto, templateId?: string) => {
    if (templateId) {
      await updateMutation.mutateAsync({
        id: templateId,
        data: {
          name: data.name,
          context: data.context,
          type: data.type,
          templateJson: data.templateJson,
          selectionCriteria: data.selectionCriteria,
        },
      });
    } else {
      await createMutation.mutateAsync(data);
    }
    setModalOpen(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this slide template?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <OrganizationContentStack
      content={
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <PageHeaderBar
            mainContent={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SlideshowOutlined sx={{ fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Slide Templates
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate} sx={{ flexShrink: 0 }}>
                  Create
                </Button>
              </Box>
            }
          />

          <Card sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CardContent
              sx={{
                'flex': 1,
                'minHeight': 0,
                'display': 'flex',
                'flexDirection': 'column',
                'overflow': 'hidden',
                'p': 0,
                '&:last-child': { pb: 0 },
              }}
            >
              {isLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography>Loading...</Typography>
                </Box>
              ) : error ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="error">Error loading templates.</Typography>
                </Box>
              ) : !templates || templates.length === 0 ? (
                <Box sx={{ p: 4 }}>
                  <EmptyState
                    text="No slide templates yet"
                    icon={<SlideshowOutlined sx={{ fontSize: 48, color: 'text.secondary' }} />}
                  />
                </Box>
              ) : (
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Slug</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Context</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Layers</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Capacity</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {templates.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>
                            <Typography variant="body2">{t.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {t.slug}
                            </Typography>
                          </TableCell>
                          <TableCell>{t.context}</TableCell>
                          <TableCell>{t.type}</TableCell>
                          <TableCell>{(t.templateJson?.layers as unknown[])?.length ?? 0}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {(() => {
                                const sc = t.selectionCriteria as
                                  | { mediaCapacity?: number; roomCamCapacity?: number; remoteSpeakerCapacity?: number }
                                  | undefined;
                                const parts = [];
                                if (sc?.mediaCapacity != null) parts.push(`M:${sc.mediaCapacity}`);
                                if (sc?.roomCamCapacity != null) parts.push(`R:${sc.roomCamCapacity}`);
                                if (sc?.remoteSpeakerCapacity != null) parts.push(`S:${sc.remoteSpeakerCapacity}`);
                                return parts.length ? parts.join(' ') : '—';
                              })()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <IconButton size="small" onClick={() => handleOpenEdit(t)} aria-label="Edit">
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(t.id)}
                                aria-label="Delete"
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <SlideTemplateFormModal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setEditing(null);
            }}
            template={editing}
            onSave={handleSave}
            isSaving={isSaving}
          />
        </Box>
      }
    />
  );
}
